/**
 * Phase 2 · Recipe management: bills of materials with ingredient and
 * sub-recipe lines, wastage, and live auto-costing (cost per portion, food
 * cost %, margin, suggested price) recomputed server-side on every save.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { http, ApiError } from '../api/client';
import { useAuth } from '../store/auth';
import { useUi } from '../store/ui';
import { money, num, pct } from '../lib/format';
import { DataTable, type Column } from '../components/ui/DataTable';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Input, Select, Textarea } from '../components/ui/Input';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

interface Recipe {
  id: string;
  name: string;
  is_sub_recipe: boolean;
  menu_item_name: string | null;
  yield_qty: number;
  yield_unit: string;
  wastage_pct: number;
  cost_per_portion: number;
  food_cost_pct: number | null;
  margin_pct: number | null;
  suggested_price: number | null;
}
interface RecipeLine {
  id?: string;
  ingredient_id: string | null;
  sub_recipe_id: string | null;
  quantity: number;
  unit_symbol: string;
  is_optional?: boolean;
}
interface Ingredient { id: string; name: string; unit_symbol: string; unit_cost: number }

export function RecipesPage() {
  const can = useAuth((s) => s.user?.permissions.includes('recipes:manage') ?? false);
  const toast = useUi((s) => s.toast);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<null | { id?: string }>(null);
  const [toDelete, setToDelete] = useState<Recipe | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get<{ recipes: Recipe[] }>('/recipes');
      setRecipes(r.recipes);
    } catch (e) {
      toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? recipes.filter((r) => r.name.toLowerCase().includes(s)) : recipes;
  }, [recipes, search]);

  const cols: Column<Recipe>[] = [
    { key: 'name', header: 'Recipe', sortable: true, render: (r) => (
      <div>
        <p className="font-medium text-ink">{r.name}</p>
        <p className="text-xs text-ink-muted">{r.menu_item_name ? `linked to ${r.menu_item_name}` : 'not linked'}</p>
      </div>
    ) },
    { key: 'is_sub_recipe', header: 'Type', render: (r) => r.is_sub_recipe ? <Badge tone="info">Sub-recipe</Badge> : <Badge>Dish</Badge> },
    { key: 'yield_qty', header: 'Yield', render: (r) => `${num(r.yield_qty, 2)} ${r.yield_unit}` },
    { key: 'cost_per_portion', header: 'Cost / portion', render: (r) => money(r.cost_per_portion) },
    { key: 'food_cost_pct', header: 'Food cost', render: (r) => (r.food_cost_pct === null ? '—' : pct(r.food_cost_pct)) },
    { key: 'margin_pct', header: 'Margin', render: (r) => (r.margin_pct === null ? '—' : pct(r.margin_pct)) },
    ...(can ? [{
      key: 'actions', header: '', render: (r: Recipe) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setToDelete(r)} aria-label={`Delete ${r.name}`}><Trash2 size={14} /></Button>
          <Button size="sm" variant="secondary" onClick={() => setEditor({ id: r.id })}>Edit</Button>
        </div>
      ),
    }] as Column<Recipe>[] : []),
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Recipes & costing</h1>
          <p className="text-sm text-ink-muted">{recipes.length} recipes · costs recompute on every save</p>
        </div>
        {can && <Button onClick={() => setEditor({})}><Plus size={16} /> New recipe</Button>}
      </header>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <Input aria-label="Search recipes" className="pl-9" placeholder="Search recipes…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <DataTable columns={cols} rows={filtered} loading={loading}
        onRowClick={can ? (r) => setEditor({ id: r.id }) : undefined} />

      {editor && <RecipeModal id={editor.id} onClose={() => setEditor(null)} onSaved={async () => { setEditor(null); await load(); }} />}
      {toDelete && (
        <ConfirmDialog open onClose={() => setToDelete(null)} title="Delete recipe"
          message={`Delete "${toDelete.name}"? Lines that use it as a sub-recipe will block deletion.`}
          confirmLabel="Delete" onConfirm={async () => {
            try {
              await http.del(`/recipes/${toDelete.id}`);
              toast({ kind: 'success', title: 'Recipe deleted', message: toDelete.name });
              await load();
            } catch (e) {
              toast({ kind: 'error', title: 'Delete failed', message: e instanceof ApiError ? e.message : String(e) });
            }
          }} />
      )}
    </div>
  );
}

interface EditorLine { kind: 'ingredient' | 'sub'; refId: string; quantity: string; unit: string }

function RecipeModal({ id, onClose, onSaved }: { id?: string; onClose: () => void; onSaved: () => Promise<void> }) {
  const toast = useUi((s) => s.toast);
  const editing = Boolean(id);
  const [name, setName] = useState('');
  const [isSub, setIsSub] = useState(false);
  const [yieldQty, setYieldQty] = useState('1');
  const [yieldUnit, setYieldUnit] = useState('pcs');
  const [wastage, setWastage] = useState('0');
  const [steps, setSteps] = useState('');
  const [lines, setLines] = useState<EditorLine[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [subRecipes, setSubRecipes] = useState<Recipe[]>([]);
  const units = ['g', 'kg', 'ml', 'l', 'pcs'];
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [ing, subs] = await Promise.all([
          http.get<{ ingredients: Ingredient[] }>('/inventory/ingredients'),
          http.get<{ recipes: Recipe[] }>('/recipes', { sub: 'true' }),
        ]);
        setIngredients(ing.ingredients);
        setSubRecipes(subs.recipes);
        if (id) {
          const d = await http.get<{ recipe: Recipe & { lines: RecipeLine[] } }>(`/recipes/${id}`);
          const r = d.recipe;
          setName(r.name); setIsSub(r.is_sub_recipe);
          setYieldQty(String(r.yield_qty)); setYieldUnit(r.yield_unit);
          setWastage(String(r.wastage_pct)); setSteps((r as { steps?: string }).steps ?? '');
          setLines(r.lines.map((l) => ({
            kind: l.ingredient_id ? 'ingredient' : 'sub',
            refId: (l.ingredient_id ?? l.sub_recipe_id)!,
            quantity: String(l.quantity),
            unit: l.unit_symbol,
          })));
        }
      } catch (e) {
        toast({ kind: 'error', title: 'Load failed', message: e instanceof ApiError ? e.message : String(e) });
      }
    })();
  }, [id, toast]);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name, is_sub_recipe: isSub,
        yield_qty: parseFloat(yieldQty || '1'), yield_unit: yieldUnit,
        wastage_pct: parseFloat(wastage || '0'), steps: steps || null,
        lines: lines.map((l) => ({
          ingredient_id: l.kind === 'ingredient' ? l.refId : null,
          sub_recipe_id: l.kind === 'sub' ? l.refId : null,
          quantity: parseFloat(l.quantity || '0'),
          unit_symbol: l.unit,
        })),
      };
      if (editing) await http.patch(`/recipes/${id}`, body);
      else await http.post('/recipes', body);
      toast({ kind: 'success', title: editing ? 'Recipe updated' : 'Recipe created', message: name });
      await onSaved();
    } catch (e) {
      toast({ kind: 'error', title: 'Save failed', message: e instanceof ApiError ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={editing ? 'Edit recipe' : 'New recipe'} size="lg"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={() => void save()}>Save & re-cost</Button></>}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <label className="flex items-end gap-2 pb-2 text-sm text-ink">
          <input type="checkbox" checked={isSub} onChange={(e) => setIsSub(e.target.checked)} />
          Sub-recipe (reusable component)
        </label>
        <Input label="Yield quantity" type="number" min="0.000001" step="any" value={yieldQty} onChange={(e) => setYieldQty(e.target.value)} />
        <Select label="Yield unit" value={yieldUnit} onChange={(e) => setYieldUnit(e.target.value)}>
          {units.map((u) => <option key={u}>{u}</option>)}
        </Select>
        <Input label="Wastage %" type="number" min="0" max="100" value={wastage} onChange={(e) => setWastage(e.target.value)} />
        <div className="sm:col-span-2"><Textarea label="Method / steps" rows={3} value={steps} onChange={(e) => setSteps(e.target.value)} /></div>
      </div>

      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">Ingredients & sub-recipes</h3>
          <Button size="sm" variant="secondary" onClick={() => setLines((ls) => [...ls, { kind: 'ingredient', refId: ingredients[0]?.id ?? '', quantity: '100', unit: 'g' }])}>
            <Plus size={14} /> Add line
          </Button>
        </div>
        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 sm:grid-cols-[7rem_1fr_6rem_6rem_2.5rem]">
              <Select aria-label="Line kind" value={l.kind} onChange={(e) => {
                const kind = e.target.value as 'ingredient' | 'sub';
                setLines((ls) => ls.map((x, j) => j === i ? { ...x, kind, refId: kind === 'ingredient' ? (ingredients[0]?.id ?? '') : (subRecipes[0]?.id ?? ''), unit: kind === 'ingredient' ? x.unit : 'pcs' } : x));
              }}>
                <option value="ingredient">Ingredient</option>
                <option value="sub">Sub-recipe</option>
              </Select>
              <Select aria-label="Reference" value={l.refId} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, refId: e.target.value } : x))}>
                {(l.kind === 'ingredient' ? ingredients : subRecipes).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </Select>
              <Input aria-label="Quantity" type="number" step="any" min="0" value={l.quantity} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
              <Select aria-label="Unit" value={l.unit} onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))}>
                {units.map((u) => <option key={u}>{u}</option>)}
              </Select>
              <Button size="sm" variant="ghost" aria-label="Remove line" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 size={14} /></Button>
            </div>
          ))}
          {!lines.length && <p className="text-sm text-ink-muted">No lines yet — add ingredients to enable costing.</p>}
        </div>
      </div>
    </Modal>
  );
}
